import api from './api';

// ============================================================
// Service da camada de integracoes externas (T1 / S2).
// Espelha os endpoints de back/controller/integracoes/Controller.Integracao.pas:
//   GET    /v1/integracoes
//   GET    /v1/integracoes/:id
//   POST   /v1/integracoes/:tipo/conectar
//   POST   /v1/integracoes/:id/desconectar
//   GET    /v1/integracoes/:id/eventos?limit=50
//   POST   /v1/integracoes/:id/eventos/:eventoId/retry
//   GET    /v1/integracoes/queue/status
//   POST   /v1/integracoes/teste/mock
// Mercado Livre: rotas /v1/integracoes/ml/* (ver bloco proprio abaixo).
//
// Convencoes:
//   - JWT vai automaticamente via interceptor do api.js
//   - Servidor retorna 404 em recurso de outro tenant; UI trata como "nao encontrado"
//   - Funcoes que falham deixam o erro propagar (caller usa try/catch + toast)
// ============================================================

const BASE = '/v1/integracoes';

export const listarIntegracoes = async () => {
  const { data } = await api.get(BASE);
  return data;
};

export const buscarIntegracao = async (id) => {
  const { data } = await api.get(`${BASE}/${id}`);
  return data;
};

export const conectarIntegracao = async (tipo) => {
  const { data } = await api.post(`${BASE}/${encodeURIComponent(tipo)}/conectar`);
  return data;
};

export const desconectarIntegracao = async (id) => {
  const { data } = await api.post(`${BASE}/${id}/desconectar`);
  return data;
};

export const listarEventos = async (id, limit = 50) => {
  const { data } = await api.get(`${BASE}/${id}/eventos`, { params: { limit } });
  return data;
};

export const retentarEvento = async (idIntegracao, idEvento) => {
  const { data } = await api.post(`${BASE}/${idIntegracao}/eventos/${idEvento}/retry`);
  return data;
};

export const obterStatusFila = async () => {
  const { data } = await api.get(`${BASE}/queue/status`);
  return data;
};

// ============================================================
// Mercado Livre (T1 / S3-S4) - back/controller/integracoes/Controller.MercadoLivre.pas
// ============================================================
const ML = `${BASE}/ml`;

// Devolve { url } da tela de autorizacao do ML. O navegador vai para la e o
// backend devolve para /inicio/integracao?ml=conectado|erro.
export const iniciarConexaoML = async () => {
  const { data } = await api.get(`${ML}/auth/start`);
  return data;
};

// { vinculados: [...], sem_vinculo: [...], importado_em, ultima_busca_pedidos }
export const listarAnunciosML = async () => {
  const { data } = await api.get(`${ML}/anuncios`);
  return data;
};

export const importarAnunciosML = async () => {
  const { data } = await api.post(`${ML}/anuncios/importar`);
  return data;
};

export const vincularAnuncioML = async (idExterno, idproduto) => {
  const { data } = await api.post(`${ML}/anuncios/vincular`, { id_externo: idExterno, idproduto });
  return data;
};

export const desvincularAnuncioML = async (idExterno) => {
  const { data } = await api.post(`${ML}/anuncios/desvincular`, { id_externo: idExterno });
  return data;
};

export const sincronizarPedidosML = async () => {
  const { data } = await api.post(`${ML}/pedidos/sincronizar`);
  return data;
};

// { idnatureza, baixar_estoque }
export const lerConfigML = async () => {
  const { data } = await api.get(`${ML}/config`);
  return data;
};

export const salvarConfigML = async (config) => {
  const { data } = await api.put(`${ML}/config`, config);
  return data;
};

export const enfileirarEventoMock = async (tipoEvento, payload = {}) => {
  const { data } = await api.post(`${BASE}/teste/mock`, {
    tipo_evento: tipoEvento,
    payload,
  });
  return data;
};

// ============================================================
// Catalogo de integracoes que o hub exibe.
// Mantemos no front uma lista mestra de canais com label/icone/disponibilidade
// porque o backend so retorna o que ja foi conectado ao tenant. Para mostrar
// "em breve" antes do primeiro Conectar, precisamos da lista completa.
// ============================================================
export const CATALOGO_INTEGRACOES = [
  {
    tipo: 'ml',
    nome: 'Mercado Livre',
    descricao: 'Pedidos entram no Phada e o estoque do Phada atualiza os anúncios.',
    disponivel: true,
    icone: 'shopping-bag',
    cor: '#FFE600',
  },
  {
    tipo: 'melhor_envio',
    nome: 'Melhor Envio',
    descricao: 'Cotacao de frete, etiquetas e tracking.',
    disponivel: false, // habilita em S5
    icone: 'truck',
    cor: '#0E7AFE',
  },
  {
    tipo: 'whatsapp',
    nome: 'WhatsApp Business',
    descricao: 'Envio de NF-e, boleto e segunda via.',
    disponivel: false, // habilita em S5
    icone: 'message-circle',
    cor: '#25D366',
  },
  {
    tipo: 'mock',
    nome: 'Conector de Teste',
    descricao: 'Apenas em ambiente de desenvolvimento - valida o motor da fila.',
    disponivel: true,
    apenasDev: true,
    icone: 'tool',
    cor: '#888',
  },
];

// Mapeia status do backend para badge/cor no front.
export const STATUS_BADGE = {
  desconectado: { label: 'Desconectado', cor: 'secondary' },
  conectando: { label: 'Conectando', cor: 'info' },
  conectado: { label: 'Conectado', cor: 'success' },
  erro: { label: 'Erro', cor: 'danger' },
  expirado: { label: 'Expirado', cor: 'warning' },
};

export const STATUS_EVENTO_BADGE = {
  pending: { label: 'Pendente', cor: 'secondary' },
  processing: { label: 'Processando', cor: 'info' },
  done: { label: 'OK', cor: 'success' },
  error: { label: 'Erro', cor: 'warning' },
  dead: { label: 'Falhou', cor: 'danger' },
};

const integracaoService = {
  listarIntegracoes,
  buscarIntegracao,
  conectarIntegracao,
  desconectarIntegracao,
  listarEventos,
  retentarEvento,
  obterStatusFila,
  enfileirarEventoMock,
  iniciarConexaoML,
  listarAnunciosML,
  importarAnunciosML,
  vincularAnuncioML,
  desvincularAnuncioML,
  sincronizarPedidosML,
  lerConfigML,
  salvarConfigML,
  CATALOGO_INTEGRACOES,
  STATUS_BADGE,
  STATUS_EVENTO_BADGE,
};

export default integracaoService;
